import React, { useMemo, useState } from "react";
import "./EstadoCuentaPDF.css";
import { EncabezadoPagina, Iconos } from "../componentes/base";
import { authFetch } from "../utils/authFetch";

function fmtMoney(value) {
  return Number(value || 0).toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN",
  });
}

function badgeClass(tipo) {
  return tipo === "cargo" ? "ecpdf-badge danger" : "ecpdf-badge success";
}

function warningClass(status) {
  if (status === "confirmado") return "ecpdf-note info";
  if (status === "probable") return "ecpdf-note warn";
  return "ecpdf-note";
}

export default function EstadoCuentaPDF() {
  const [pdfFile, setPdfFile] = useState(null);
  const [usarLlm, setUsarLlm] = useState(false);
  const [ordenApi, setOrdenApi] = useState("desc");
  const [ordenVista, setOrdenVista] = useState("desc");
  const [tipoFiltro, setTipoFiltro] = useState("todos");
  const [servicioFiltro, setServicioFiltro] = useState("todos");
  const [matchFiltro, setMatchFiltro] = useState("todos");
  const [refFiltro, setRefFiltro] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resultado, setResultado] = useState(null);

  function showToast(mensaje, tipo = "info") {
    window.dispatchEvent(new CustomEvent("toast", { detail: { mensaje, tipo } }));
  }

  async function analizarPdf() {
    if (!pdfFile) {
      showToast("Selecciona un PDF primero.", "info");
      return;
    }

    const formData = new FormData();
    formData.append("pdf", pdfFile);
    formData.append("usar_llm", usarLlm ? "1" : "0");
    formData.append("orden", ordenApi);

    setLoading(true);
    setError("");

    try {
      const res = await authFetch("/api/estado-cuenta/analizar", {
        method: "POST",
        body: formData,
      });
      const data = await res.json();

      if (!res.ok || !data.ok) {
        const msg = data?.error || data?.mensaje || "No se pudo analizar el PDF";
        setError(msg);
        setResultado(null);
        showToast(msg, "error");
        setLoading(false);
        return;
      }

      setResultado(data);
      setOrdenVista(data?.resumen?.orden || ordenApi);
      showToast(`Movimientos detectados: ${data?.resumen?.movimientos || 0}`, "success");
      setLoading(false);
    } catch (err) {
      setError("Error de red analizando el PDF");
      setResultado(null);
      showToast("Error de red analizando el PDF", "error");
      setLoading(false);
    }
  }

  function limpiar() {
    setPdfFile(null);
    setUsarLlm(false);
    setOrdenApi("desc");
    setOrdenVista("desc");
    setTipoFiltro("todos");
    setServicioFiltro("todos");
    setMatchFiltro("todos");
    setRefFiltro("");
    setResultado(null);
    setError("");
  }

  const movimientosFiltrados = useMemo(() => {
    const rows = Array.isArray(resultado?.movimientos) ? [...resultado.movimientos] : [];

    const filtrados = rows.filter((row) => {
      if (tipoFiltro !== "todos" && row.tipo !== tipoFiltro) return false;

      const servicio = String(row?.cliente_sugerido?.tipo_servicio || "").toLowerCase();
      if (servicioFiltro === "fibra" && servicio !== "fibra") return false;
      if (servicioFiltro === "wifi" && servicio !== "wifi") return false;
      if (servicioFiltro === "sin_asignar" && servicio) return false;

      const tieneMatch = Boolean(row.cliente_sugerido);
      if (matchFiltro === "match" && !tieneMatch) return false;
      if (matchFiltro === "pendiente" && tieneMatch) return false;

      if (refFiltro.trim()) {
        const needle = refFiltro.trim().toLowerCase();
        const refMain = String(row.referencia_detectada || "").toLowerCase();
        const refs = Array.isArray(row.referencias_detectadas)
          ? row.referencias_detectadas.map((item) => String(item.valor || "").toLowerCase()).join(" ")
          : "";
        const nombre = String(row?.cliente_sugerido?.nombre || "").toLowerCase();
        const clienteId = String(row?.cliente_sugerido?.id || "").toLowerCase();
        const descripcion = String(row.descripcion || "").toLowerCase();

        if (
          !refMain.includes(needle) &&
          !refs.includes(needle) &&
          !nombre.includes(needle) &&
          !clienteId.includes(needle) &&
          !descripcion.includes(needle)
        ) {
          return false;
        }
      }

      return true;
    });

    filtrados.sort((a, b) => {
      const left = `${a.fecha_iso} ${String(a.indice).padStart(6, "0")}`;
      const right = `${b.fecha_iso} ${String(b.indice).padStart(6, "0")}`;
      return ordenVista === "asc" ? left.localeCompare(right) : right.localeCompare(left);
    });

    return filtrados;
  }, [resultado, tipoFiltro, servicioFiltro, matchFiltro, refFiltro, ordenVista]);

  const resumen = resultado?.resumen;

  return (
    <div className="ecpdf-page">
      <EncabezadoPagina
        icono={Iconos.documento}
        titulo="Estado de cuenta PDF"
        resumen="Sube el PDF del banco, ordénalo por fecha y revisa coincidencias por fibra o wifi."
      />

      <div className="ecpdf-upload-card">

        <div className="ecpdf-upload-grid">
          <label className="ecpdf-upload-box">
            <span className="upload-title">Adjuntar PDF</span>
            <span className="upload-sub">
              {pdfFile ? pdfFile.name : "Selecciona el estado de cuenta"}
            </span>
            <input
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
            />
          </label>

          <div className="ecpdf-toolbar">
            <div className="ecpdf-field">
              <label>Orden backend</label>
              <select value={ordenApi} onChange={(e) => setOrdenApi(e.target.value)}>
                <option value="desc">Más reciente primero</option>
                <option value="asc">Más antiguo primero</option>
              </select>
            </div>

            <label className="ecpdf-check">
              <input
                type="checkbox"
                checked={usarLlm}
                onChange={(e) => setUsarLlm(e.target.checked)}
              />
              <span>Usar apoyo LLM si está configurado</span>
            </label>
          </div>

          <div className="ecpdf-actions">
            <button
              className="ecpdf-primary"
              type="button"
              onClick={analizarPdf}
              disabled={loading}
            >
              {loading ? "Analizando..." : "Analizar PDF"}
            </button>
            <button className="ecpdf-ghost" type="button" onClick={limpiar} disabled={loading}>
              Limpiar
            </button>
          </div>
        </div>
      </div>

      {error && <div className="ecpdf-error">{error}</div>}

      {resumen && (
        <>
          <div className="ecpdf-stats">
            <article className="ecpdf-stat green">
              <span>Total abonos</span>
              <strong>{fmtMoney(resumen.total_abonos)}</strong>
            </article>
            <article className="ecpdf-stat rose">
              <span>Total cargos</span>
              <strong>{fmtMoney(resumen.total_cargos)}</strong>
            </article>
            <article className="ecpdf-stat amber">
              <span>Coincidencias / pendientes</span>
              <strong>
                {resumen.coincidencias} / {resumen.pendientes}
              </strong>
            </article>
          </div>

          <div className="ecpdf-filters">
            <div className="ecpdf-field">
              <label>Filtrar por tipo</label>
              <select value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)}>
                <option value="todos">Todos</option>
                <option value="abono">Abonos</option>
                <option value="cargo">Cargos</option>
              </select>
            </div>

            <div className="ecpdf-field">
              <label>Tipo de servicio</label>
              <select value={servicioFiltro} onChange={(e) => setServicioFiltro(e.target.value)}>
                <option value="todos">Todos</option>
                <option value="fibra">Fibra</option>
                <option value="wifi">Wifi</option>
                <option value="sin_asignar">Sin asignar</option>
              </select>
            </div>

            <div className="ecpdf-field">
              <label>Coincidencias</label>
              <select value={matchFiltro} onChange={(e) => setMatchFiltro(e.target.value)}>
                <option value="todos">Todos</option>
                <option value="match">Solo identificados</option>
                <option value="pendiente">Solo pendientes</option>
              </select>
            </div>

            <div className="ecpdf-field">
              <label>Orden vista</label>
              <select value={ordenVista} onChange={(e) => setOrdenVista(e.target.value)}>
                <option value="desc">Más reciente primero</option>
                <option value="asc">Más antiguo primero</option>
              </select>
            </div>

            <div className="ecpdf-field grow">
              <label>Buscar referencia / cliente</label>
              <input
                value={refFiltro}
                onChange={(e) => setRefFiltro(e.target.value)}
                placeholder="Ej: 1314, cliente 24, wifi2032, Jaime Zavala"
              />
            </div>
          </div>

          <div className="ecpdf-meta">
            <span>
              Periodo detectado: <b>{resumen.fecha_desde}</b> a <b>{resumen.fecha_hasta}</b>
            </span>
            <span>
              LLM:{" "}
              <b>
                {resumen.llm_usado
                  ? "usado"
                  : resumen.llm_disponible
                    ? "disponible"
                    : "no configurado"}
              </b>
            </span>
            {resumen.llm_error && <span>Error LLM: {resumen.llm_error}</span>}
          </div>

          <div className="ecpdf-table-card">
            <div className="ecpdf-table-header">
              <h2>Resultados del estado de cuenta</h2>
              <span>
                {movimientosFiltrados.length.toLocaleString("es-MX")} visibles de{" "}
                {Number(resumen.movimientos || 0).toLocaleString("es-MX")}
              </span>
            </div>

            <div className="ecpdf-table-wrap">
              <table className="ecpdf-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Tipo</th>
                    <th>Monto</th>
                    <th>Referencia</th>
                    <th>Cliente sugerido</th>
                    <th>Servicio</th>
                    <th>Confianza</th>
                    <th>Movimiento</th>
                  </tr>
                </thead>
                <tbody>
                  {movimientosFiltrados.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="empty">
                        No hay movimientos para los filtros actuales.
                      </td>
                    </tr>
                  ) : (
                    movimientosFiltrados.map((row) => (
                      <tr key={`${row.fecha_iso}-${row.indice}`}>
                        <td className="mono ecpdf-date">{row.fecha.replaceAll("-", "/")}</td>
                        <td>
                          <span className={badgeClass(row.tipo)}>{row.tipo}</span>
                        </td>
                        <td className="mono">{fmtMoney(row.monto)}</td>
                        <td>
                          <div className="ecpdf-ref-main">{row.referencia_detectada || "Sin detectar"}</div>
                          <div className="ecpdf-ref-list">
                            {Array.isArray(row.referencias_detectadas) && row.referencias_detectadas.length > 0
                              ? row.referencias_detectadas.map((item) => item.valor).join(", ")
                              : "—"}
                          </div>
                        </td>
                        <td>
                          {row.cliente_sugerido ? (
                            <div className="ecpdf-client">
                              <strong>
                                #{row.cliente_sugerido.id} · {row.cliente_sugerido.nombre}
                              </strong>
                              {row.advertencias?.[0] && (
                                <div className="ecpdf-note warn">{row.advertencias[0]}</div>
                              )}
                              {row.duplicado?.message && (
                                <div className={warningClass(row.duplicado.status)}>
                                  {row.duplicado.message}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="ecpdf-client">
                              {Array.isArray(row.coincidencias) && row.coincidencias.length > 0 ? (
                                <>
                                  <span className="ecpdf-review-tag">Revisar candidatos</span>
                                  <div className="ecpdf-candidates">
                                    {row.coincidencias.slice(0, 3).map((cand) => (
                                      <div
                                        className="ecpdf-candidate"
                                        key={`${cand.id}-${cand.tipo_servicio}`}
                                      >
                                        <span className="ecpdf-candidate-name">
                                          #{cand.id} · {cand.nombre}
                                        </span>
                                        <span className="ecpdf-candidate-meta">
                                          {cand.tipo_servicio} · {cand.source} ·{" "}
                                          {Math.round((cand.confidence || 0) * 100)}%
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              ) : (
                                <span className="muted">Sin coincidencia</span>
                              )}
                              {row.advertencias?.[0] && (
                                <div className="ecpdf-note warn">{row.advertencias[0]}</div>
                              )}
                            </div>
                          )}
                        </td>
                        <td>
                          {row.cliente_sugerido?.tipo_servicio ? (
                            <span className={`ecpdf-service ${row.cliente_sugerido.tipo_servicio}`}>
                              {row.cliente_sugerido.tipo_servicio}
                            </span>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                        <td>
                          {row.cliente_sugerido ? (
                            <div className="ecpdf-confidence">
                              <strong>{Math.round((row.cliente_sugerido.confidence || 0) * 100)}%</strong>
                              <span>{row.cliente_sugerido.source}</span>
                            </div>
                          ) : (
                            <div className="ecpdf-confidence">
                              <strong>—</strong>
                              <span>{row.coincidencias?.length ? `${row.coincidencias.length} opción(es)` : "sin match"}</span>
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="ecpdf-desc">{row.concepto}</div>
                          {row.detalle && <div className="ecpdf-detail">{row.detalle}</div>}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
